<template>
  <div>
    <div class="py-3"></div>
    <v-card class="mx-auto" max-width="95%">
      <v-overlay :value="loading" absolute opacity="0.7">
        <v-progress-circular indeterminate size="48" />
      </v-overlay>
      <v-toolbar color="light-blue darken-3" dark>
        <v-toolbar-title>쿠폰 주문/매출</v-toolbar-title>
      </v-toolbar>

      <v-tabs v-model="tab" background-color="light-blue lighten-5" color="light-blue darken-3">
        <v-tab>주문 목록</v-tab>
        <v-tab>통계</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- ===== 탭1: 주문 목록 ===== -->
        <v-tab-item>
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="2">
                <v-select
                  v-model="orderFilter.app_pid"
                  :items="appItems"
                  item-text="text"
                  item-value="value"
                  label="앱"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-select
                  v-model="orderFilter.status"
                  :items="statusItems"
                  item-text="text"
                  item-value="value"
                  label="상태"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="orderFilter.from_date"
                  label="시작일"
                  type="date"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="orderFilter.to_date"
                  label="종료일"
                  type="date"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="orderFilter.user_key"
                  label="유저키"
                  outlined
                  dense
                  hide-details
                  clearable
                  @keyup.enter="loadOrders"
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-btn color="primary" block :disabled="loading" @click="loadOrders">
                  <v-icon left>mdi-magnify</v-icon>
                  조회
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-text class="pa-0">
            <v-data-table
              v-if="orders.length > 0"
              :headers="orderHeaders"
              :items="orders"
              :items-per-page="20"
              class="elevation-0"
              :footer-props="{ 'items-per-page-options': [20, 50, 100, -1] }"
            >
              <template v-slot:item.ORDER_DATE="{ item }">
                {{ item.ORDER_DATE || '-' }}
              </template>
              <template v-slot:item.APP_PID="{ item }">
                {{ appName(item.APP_PID) }}
              </template>
              <template v-slot:item.USER_KEY="{ item }">
                <span class="text-body-2">{{ item.USER_KEY || '-' }}</span>
              </template>
              <template v-slot:item.RCV_CTN="{ item }">
                <span class="text-body-2">{{ item.RCV_CTN || '-' }}</span>
              </template>
              <template v-slot:item.PRODUCT_NAME="{ item }">
                {{ item.PRODUCT_NAME || '-' }}
              </template>
              <template v-slot:item.AMOUNT="{ item }">
                {{ formatAmount(item.AMOUNT) }}
              </template>
              <template v-slot:item.STATUS="{ item }">
                <div>
                  <v-chip small dark :color="statusColor(item.STATUS)">
                    {{ item.STATUS || '-' }}
                  </v-chip>
                  <div
                    v-if="isFailStatus(item.STATUS) && item.FAIL_REASON"
                    class="text-caption grey--text mt-1"
                    :title="item.FAIL_REASON"
                  >
                    {{ item.FAIL_REASON }}
                  </div>
                </div>
              </template>
              <template v-slot:item.USER_EXPIRE_AT="{ item }">
                {{ item.USER_EXPIRE_AT || '-' }}
              </template>
              <template v-slot:item.DAOU_EXPIRE_AT="{ item }">
                {{ item.DAOU_EXPIRE_AT || '-' }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  x-small
                  color="primary"
                  outlined
                  class="mr-1"
                  @click="openDetail(item)"
                >
                  <v-icon x-small left>mdi-information-outline</v-icon>상세
                </v-btn>
                <v-btn
                  x-small
                  color="error"
                  outlined
                  class="mr-1"
                  :disabled="loading || item.STATUS !== 'ISSUED'"
                  @click="cancelOrder(item)"
                >
                  <v-icon x-small left>mdi-cancel</v-icon>취소
                </v-btn>
                <v-btn
                  x-small
                  color="grey darken-1"
                  outlined
                  :disabled="loading || !item.COOPER_ORDER"
                  title="다우 상태 조회 후 갱신"
                  @click="refreshOrder(item)"
                >
                  <v-icon x-small>mdi-refresh</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-container v-else class="text-center py-8">
              <v-icon size="64" color="grey lighten-1">mdi-ticket-confirmation-outline</v-icon>
              <p class="text-body-1 grey--text mt-3">조회된 주문이 없습니다.</p>
            </v-container>
          </v-card-text>
        </v-tab-item>

        <!-- ===== 탭2: 통계 ===== -->
        <v-tab-item>
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="statFilter.from_date"
                  label="시작일"
                  type="date"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="statFilter.to_date"
                  label="종료일"
                  type="date"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-btn color="primary" block :disabled="loading" @click="loadStats">
                  <v-icon left>mdi-magnify</v-icon>
                  조회
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-text class="pa-0">
            <v-data-table
              v-if="stats.length > 0"
              :headers="statHeaders"
              :items="stats"
              :items-per-page="30"
              class="elevation-0"
              :footer-props="{ 'items-per-page-options': [30, 100, -1] }"
            >
              <template v-slot:item.ORDER_DATE="{ item }">
                {{ item.ORDER_DATE || '-' }}
              </template>
              <template v-slot:item.APPNAME="{ item }">
                {{ item.APPNAME || appName(item.APP_PID) }}
              </template>
              <template v-slot:item.ORDER_COUNT="{ item }">
                {{ formatAmount(item.ORDER_COUNT) }}
              </template>
              <template v-slot:item.TOTAL_AMOUNT="{ item }">
                {{ formatAmount(item.TOTAL_AMOUNT) }}
              </template>
              <template v-slot:item.RECLAIMED_COUNT="{ item }">
                {{ formatAmount(item.RECLAIMED_COUNT) }}
              </template>
            </v-data-table>

            <v-container v-else class="text-center py-8">
              <v-icon size="64" color="grey lighten-1">mdi-chart-bar</v-icon>
              <p class="text-body-1 grey--text mt-3">조회된 통계가 없습니다.</p>
            </v-container>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- ================= 주문 상세 다이얼로그 ================= -->
    <v-dialog v-model="detailDialog" max-width="640" scrollable>
      <v-card v-if="detailItem">
        <v-toolbar color="light-blue darken-3" dark dense flat>
          <v-toolbar-title class="text-subtitle-1">주문 상세 · {{ appName(detailItem.APP_PID) }}</v-toolbar-title>
          <v-spacer />
          <v-chip small dark :color="statusColor(detailItem.STATUS)">{{ detailItem.STATUS || '-' }}</v-chip>
          <v-btn icon class="ml-2" @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <!-- 상품 요약 -->
          <div class="d-flex align-center mb-4">
            <v-avatar tile size="56" class="mr-3" color="grey lighten-3">
              <img v-if="detailItem.GOODS_IMAGE" :src="detailItem.GOODS_IMAGE" style="object-fit: contain;" alt="상품" />
              <v-icon v-else color="grey">mdi-ticket-percent-outline</v-icon>
            </v-avatar>
            <div class="min-w-0">
              <div class="text-subtitle-1 font-weight-medium">{{ detailItem.PRODUCT_NAME || '-' }}</div>
              <div class="text-caption grey--text">
                {{ detailItem.BRAND_NAME || '-' }} · 액면가 {{ formatAmount(detailItem.FACE_VALUE) }}원
              </div>
            </div>
            <v-spacer />
            <div class="text-h6 font-weight-bold light-blue--text text--darken-3">
              {{ formatAmount(detailItem.AMOUNT) }}원
            </div>
          </div>

          <v-divider class="mb-1" />
          <v-simple-table dense class="detail-table">
            <tbody>
              <tr><td class="dk">앱</td><td>{{ appName(detailItem.APP_PID) }}</td></tr>
              <tr><td class="dk">유저키</td><td>{{ detailItem.USER_KEY || '-' }}</td></tr>
              <tr><td class="dk">전화번호</td><td class="font-weight-medium">{{ detailItem.RCV_CTN || '-' }}</td></tr>
              <tr><td class="dk">주문일시</td><td>{{ detailItem.CREATED_AT || '-' }}</td></tr>
              <tr><td class="dk">최종수정</td><td>{{ detailItem.UPDATED_AT || '-' }}</td></tr>
              <tr><td class="dk">유저만료</td><td>{{ detailItem.USER_EXPIRE_AT || '-' }}</td></tr>
              <tr><td class="dk">다우소멸</td><td>{{ detailItem.DAOU_EXPIRE_AT || '-' }}</td></tr>
              <tr><td class="dk">내부주문번호</td><td class="mono">{{ detailItem.ORDER_ID || '-' }}</td></tr>
              <tr><td class="dk">다우주문번호</td><td class="mono">{{ detailItem.COOPER_ORDER || '-' }}</td></tr>
              <tr><td class="dk">쿠폰계약(NO_REQ)</td><td class="mono">{{ detailItem.NO_REQ || '-' }}</td></tr>
              <tr><td class="dk">쿠폰핀</td><td class="mono">{{ detailItem.NO_CPN || '-' }}</td></tr>
              <tr><td class="dk">발행 TS_ID</td><td class="mono">{{ detailItem.TS_ID || '-' }}</td></tr>
              <tr><td class="dk">결과코드(RT)</td><td class="mono">{{ detailItem.RT || '-' }}</td></tr>
              <tr v-if="detailItem.FAIL_REASON">
                <td class="dk">실패사유</td><td class="red--text">{{ detailItem.FAIL_REASON }}</td>
              </tr>
              <tr><td class="dk">요청 IP</td><td class="mono">{{ detailItem.CLIENT_IP || '-' }}</td></tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn
            text
            color="grey darken-1"
            :disabled="loading || !detailItem.COOPER_ORDER"
            @click="refreshOrder(detailItem)"
          >
            <v-icon left small>mdi-refresh</v-icon>상태갱신
          </v-btn>
          <v-spacer />
          <v-btn
            text
            color="error"
            :disabled="loading || detailItem.STATUS !== 'ISSUED'"
            @click="cancelOrder(detailItem)"
          >
            <v-icon left small>mdi-cancel</v-icon>주문취소
          </v-btn>
          <v-btn color="primary" text @click="detailDialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { coupon, housead } from '../../api';

export default {
  name: 'Coupon_Orders',
  data() {
    return {
      tab: 0,
      loading: false,
      apps: [],
      orders: [],
      stats: [],
      detailDialog: false,
      detailItem: null,
      orderFilter: {
        app_pid: null,
        status: '',
        from_date: '',
        to_date: '',
        user_key: '',
      },
      statFilter: {
        from_date: '',
        to_date: '',
      },
      statusItems: [
        { text: '전체', value: '' },
        { text: 'ISSUED', value: 'ISSUED' },
        { text: 'USED', value: 'USED' },
        { text: 'RECLAIMED', value: 'RECLAIMED' },
        { text: 'RECLAIM_FAILED', value: 'RECLAIM_FAILED' },
        { text: 'FAILED', value: 'FAILED' },
      ],
      orderHeaders: [
        { text: '주문일', value: 'ORDER_DATE', width: '120' },
        { text: '앱', value: 'APP_PID', width: '140' },
        { text: '유저키', value: 'USER_KEY', width: '160' },
        { text: '전화번호', value: 'RCV_CTN', width: '130' },
        { text: '상품', value: 'PRODUCT_NAME' },
        { text: '금액', value: 'AMOUNT', width: '110', align: 'end' },
        { text: '상태', value: 'STATUS', width: '160' },
        { text: '유저만료', value: 'USER_EXPIRE_AT', width: '150' },
        { text: '다우소멸', value: 'DAOU_EXPIRE_AT', width: '150' },
        { text: '관리', value: 'actions', width: '210', sortable: false, align: 'center' },
      ],
      statHeaders: [
        { text: '일자', value: 'ORDER_DATE', width: '130' },
        { text: '앱', value: 'APPNAME' },
        { text: '건수', value: 'ORDER_COUNT', width: '110', align: 'end' },
        { text: '매출', value: 'TOTAL_AMOUNT', width: '130', align: 'end' },
        { text: '회수건수', value: 'RECLAIMED_COUNT', width: '110', align: 'end' },
      ],
    };
  },
  computed: {
    appItems() {
      const list = (this.apps || []).map((a) => ({ text: a.appName || `앱 #${a.id}`, value: a.id }));
      return [{ text: '전체', value: null }].concat(list);
    },
  },
  mounted() {
    this.loadApps();
    this.loadOrders();
  },
  methods: {
    // 값이 있는 키만 담는다 (빈 문자열이 쿼리에 들어가면 백엔드 필터가 오작동)
    buildParams(source) {
      const params = {};
      Object.keys(source).forEach((key) => {
        const value = source[key];
        if (value === null || value === undefined) return;
        if (typeof value === 'string' && value.trim() === '') return;
        params[key] = typeof value === 'string' ? value.trim() : value;
      });
      return params;
    },
    appName(appPid) {
      if (appPid === null || appPid === undefined || appPid === '') return '-';
      const found = (this.apps || []).find((a) => String(a.id) === String(appPid));
      return (found && found.appName) ? found.appName : appPid;
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '-';
      const num = Number(value);
      return Number.isNaN(num) ? value : num.toLocaleString();
    },
    isFailStatus(status) {
      return status === 'FAILED' || status === 'RECLAIM_FAILED';
    },
    statusColor(status) {
      switch (status) {
        case 'ISSUED': return 'blue';
        case 'USED': return 'grey';
        case 'RECLAIMED': return 'green';
        case 'RECLAIM_FAILED':
        case 'FAILED': return 'red';
        default: return 'grey';
      }
    },
    // 앱 목록 실패는 치명적이지 않으므로 조용히 무시
    loadApps() {
      return housead.getApps()
        // getApps 는 { list:[...] } 형태로 온다 (배열 아님) → list 우선 언랩
        .then((res) => {
          this.apps = Array.isArray(res) ? res : ((res && Array.isArray(res.list)) ? res.list : []);
        })
        .catch((e) => { console.error('앱 목록 조회 실패:', e); });
    },
    loadOrders() {
      this.loading = true;
      coupon.getOrders(this.buildParams(this.orderFilter))
        .then((res) => { this.orders = (res && res.orders) ? res.orders : []; })
        .catch((e) => { alert(typeof e === 'string' ? e : '조회에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    loadStats() {
      this.loading = true;
      coupon.getOrderStats(this.buildParams(this.statFilter))
        .then((res) => { this.stats = (res && res.stats) ? res.stats : []; })
        .catch((e) => { alert(typeof e === 'string' ? e : '조회에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    // 주문 상세 다이얼로그
    openDetail(item) {
      this.detailItem = item;
      this.detailDialog = true;
    },
    // 주문 취소(다우 폐기) — ISSUED 만 가능. 되돌릴 수 없음.
    cancelOrder(item) {
      if (!item || !item.PID) return;
      if (item.STATUS !== 'ISSUED') {
        alert('발행(ISSUED) 상태만 취소할 수 있습니다.');
        return;
      }
      const ok = window.confirm(
        `이 주문을 취소(다우 폐기)하시겠습니까?\n\n`
        + `· 주문: ${item.ORDER_ID || item.PID}\n`
        + `· 상품: ${item.PRODUCT_NAME || '-'}\n`
        + `· 유저: ${item.USER_KEY || '-'}\n\n`
        + `취소하면 쿠폰이 폐기되며 되돌릴 수 없습니다.`,
      );
      if (!ok) return;
      this.detailDialog = false;
      this.loading = true;
      coupon.cancelOrder(item.PID)
        .then((res) => {
          if (res && res.cancelled) {
            alert(`취소 완료 (상태: ${res.status})`);
          } else {
            alert((res && res.message) ? res.message
              : `취소하지 못했습니다 (상태: ${(res && res.status) || '알 수 없음'})`);
          }
          return this.loadOrders();
        })
        .catch((e) => { alert(typeof e === 'string' ? e : '취소에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    // 상태 갱신 — 다우 실제 상태를 조회해 STATUS 동기화(취소 안 함)
    refreshOrder(item) {
      if (!item || !item.PID) return;
      this.detailDialog = false;
      this.loading = true;
      coupon.refreshOrderStatus(item.PID)
        .then((res) => {
          if (res && res.refreshed) {
            alert(res.changed ? `상태 갱신됨 → ${res.status}` : `변동 없음 (상태: ${res.status})`);
          } else {
            alert((res && res.message) ? res.message : '상태를 갱신하지 못했습니다.');
          }
          return this.loadOrders();
        })
        .catch((e) => { alert(typeof e === 'string' ? e : '상태 갱신에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
.min-w-0 {
  min-width: 0;
}
.detail-table >>> td {
  height: 34px;
}
.detail-table .dk {
  width: 150px;
  color: #78849a;
  background: #fafbfc;
  font-size: 0.8rem;
  white-space: nowrap;
}
.detail-table .mono {
  font-family: 'Roboto Mono', Menlo, Consolas, monospace;
  font-size: 0.82rem;
  word-break: break-all;
}
</style>
